import React, { useEffect, useMemo, useState } from "react";
import { Check, WhatsappLogo } from "@phosphor-icons/react";
import { MOCKUP, getProfessionContent } from "../../data/startPageContent";
import "./StartMockup.css";

const TIMES = ["09:00", "10:00", "11:00", "12:00", "13:00", "14:00", "15:00", "16:00"];
const TONES = ["blue", "mint", "yellow", "purple", "pink"];

/* Static layout: [dayIndex, startRow, span, nameIdx, serviceIdx, tone] */
const EVENTS = [
  [0, 0, 1, 0, 0, "blue"],
  [0, 2, 2, 1, 1, "mint"],
  [1, 1, 1, 2, 0, "yellow"],
  [1, 4, 1, 3, 2, "purple"],
  [2, 0, 2, 4, 1, "pink"],
  [2, 5, 1, 5, 0, "blue"],
  [3, 1, 1, 6, 2, "mint"],
  [3, 3, 2, 7, 0, "yellow"],
  [4, 2, 1, 0, 1, "purple"],
];
/* Event that fades in during the idle animation */
const IDLE_EVENT = [4, 5, 1, 2, 2, "pink"];

const motionAllowed = () => {
  if (typeof window === "undefined") return false;
  if (navigator.webdriver) return false;
  if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return false;
  return true;
};

/**
 * CSS-only product mockup for the /start hero: desktop week view + phone
 * overlay with a WhatsApp reminder bubble. Services follow the selected
 * profession. No raster images.
 */
export default function StartMockup({ profession = null }) {
  const [animate, setAnimate] = useState(false);

  useEffect(() => {
    if (motionAllowed()) setAnimate(true);
  }, []);

  const services = useMemo(() => {
    try {
      return getProfessionContent(profession).calendarServices;
    } catch (error) {
      console.error("[CK-1063] StartMockup personalization failed", {
        errorCode: "CK-1063",
        errorMessage: error?.message,
        profession: profession || null,
        timestamp: new Date().toISOString(),
      });
      return ["טיפול", "פגישת ייעוץ", "מפגש מעקב"];
    }
  }, [profession]);

  const renderEvent = ([day, row, span, nameIdx, serviceIdx, tone], extraClass = "") => (
    <div
      key={`${day}-${row}`}
      className={`st-mock-ev st-mock-ev--${tone} ${extraClass}`.trim()}
      style={{ gridColumn: day + 2, gridRow: `${row + 2} / span ${span}` }}
    >
      <span className="st-mock-ev-name">{MOCKUP.patientNames[nameIdx]}</span>
      <span className="st-mock-ev-svc">{services[serviceIdx % services.length]}</span>
    </div>
  );

  return (
    <div className={`st-mock${animate ? " st-mock--animate" : ""}`} aria-hidden="true">
      {/* ---- Desktop frame ---- */}
      <div className="st-mock-desktop">
        <div className="st-mock-bar">
          <span className="st-mock-logo">CliniKit</span>
          <span className="st-mock-bar-title">יומן · השבוע</span>
          <span className="st-mock-bar-dots">
            <i /><i /><i />
          </span>
        </div>
        <div className="st-mock-grid">
          <div className="st-mock-corner" />
          {MOCKUP.days.map((d, i) => (
            <div key={d} className={`st-mock-day${i === 2 ? " st-mock-day--today" : ""}`} style={{ gridColumn: i + 2, gridRow: 1 }}>
              <span>{d}</span>
              {i === 2 && <em>{MOCKUP.todayLabel}</em>}
            </div>
          ))}
          {TIMES.map((t, i) => (
            <div key={t} className="st-mock-time" style={{ gridColumn: 1, gridRow: i + 2 }}>
              {t}
            </div>
          ))}
          {TIMES.map((t, r) =>
            MOCKUP.days.map((d, c) => (
              <div key={`${r}-${c}`} className="st-mock-cell" style={{ gridColumn: c + 2, gridRow: r + 2 }} />
            ))
          )}
          {EVENTS.map((ev) => renderEvent(ev))}
          {renderEvent(IDLE_EVENT, "st-mock-ev--idle")}
          <div className="st-mock-now" style={{ gridColumn: "2 / -1", gridRow: 4 }} />
        </div>
      </div>

      {/* ---- Phone frame ---- */}
      <div className="st-mock-phone">
        <div className="st-mock-phone-notch" />
        <div className="st-mock-phone-head">
          <span className="st-mock-phone-title">{MOCKUP.todayLabel}</span>
          <span className="st-mock-phone-sub">4 פגישות</span>
        </div>
        <ul className="st-mock-phone-list">
          {[0, 1, 2].map((i) => (
            <li key={i} className={`st-mock-phone-item st-mock-phone-item--${TONES[i]}`}>
              <span className="st-mock-phone-time">{TIMES[i + 1]}</span>
              <span className="st-mock-phone-name">{MOCKUP.patientNames[i]}</span>
              <span className="st-mock-phone-svc">{services[i % services.length]}</span>
            </li>
          ))}
        </ul>
        <div className="st-mock-wa">
          <div className="st-mock-wa-head">
            <WhatsappLogo size={14} weight="fill" />
            <span>WhatsApp</span>
          </div>
          <p className="st-mock-wa-text">{MOCKUP.waBubble}</p>
          <span className="st-mock-wa-chip">
            <Check size={12} weight="bold" />
            {MOCKUP.waConfirmed}
          </span>
        </div>
      </div>
    </div>
  );
}
